// src/pages/LessonEdit.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  MapPin,
  Save,
  Loader,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

// import { Lesson } from '../components/interfaces/All';
import { useSupabase } from '../context/SupabaseContext';

const LessonEditPage: React.FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const { state, fetchLessons, updateLesson } = useSupabase();

  // Local editing state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Find lesson from global state
  const lesson = state.lessons.find(l => l.id === lessonId);
  const [notes, setNotes] = useState(lesson?.notes || '');

  useEffect(() => {
    fetchLessons();
    setLoading(false);
  }, []);

  // Handle save
  const handleSave = async () => {
    if (!lesson) return;

    setSaving(true);
    setError(null);

    try {
      // Update lesson notes
      updateLesson({...lesson, notes: notes});

      if (error) throw error;

      // Success - go back to lesson page
      fetchLessons()
      navigate(`/lesson/${lesson.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel/discard
  const handleCancel = () => {
    // navigate(`/lesson/${lesson.id}`);
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-6 flex items-center justify-center h-64">
        <Loader className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="card p-6 text-center">
          <h2 className="text-lg font-semibold mb-2">Lesson not found</h2>
          <button className="btn btn-primary" onClick={() => navigate('/lessons')}>
            Go to Lessons
          </button>
        </div>
      </div>
    );
  }



  const dateObj = new Date(lesson.time);
  const dateLabel = dateObj.toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const timeLabel = dateObj.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div className="max-w-2xl mx-auto p-1 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          className="flex items-center gap-2 text-sm text-text-light hover:text-primary transition-colors"
          onClick={() => navigate(`/lesson/${lesson.id}`)}
        >
          <ArrowLeft className="w-4 h-4" />
          Lesson Details
        </button>
      </div>

      {/* Lesson Summary (read-only) */}
      <div className="card p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1 text-sm text-text-light">
              <Calendar className="w-4 h-4" />
              {dateLabel}
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-primary" />
              <span className="text-lg font-semibold">{timeLabel}</span>
              <span className="text-xs text-text-light ml-2">{lesson.duration} min</span>
            </div>
          </div>
        </div>

        <div className="space-y-2 pt-4 border-t border-border">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-text-light" />
            <span className="font-semibold">{lesson.student}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-text-light">
            <MapPin className="w-4 h-4" />
            <span>{lesson.location}</span>
          </div>
        </div>
      </div>

      {/* Notes Editor */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Lesson Notes</h3>
          <span className="text-xs text-text-light">
            {notes.length}/500 characters
          </span>
        </div>

        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value.slice(0, 500))}
          placeholder="Add notes about this lesson (attendance, progress, homework, etc.)..."
          className="w-full p-4 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical min-h-[120px] bg-white dark:bg-gray-800"
          rows={6}
          disabled={saving}
        />

        {error && (
          <p className="text-xs text-red-500 mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
            {error}
          </p>
        )}
      </div>

      {/* Action Buttons */}
      <div className="card p-6 flex flex-col sm:flex-row gap-3 pt-0">
        <button
          className="btn btn-primary flex-1"
          onClick={handleSave}
          disabled={saving || notes.length === 0}
        >
          {saving ? (
            <>
              <Loader className="w-4 h-4 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            <>
              {/* <Save className="w-4 h-4 mr-2" /> */}
              Save
            </>
          )}
        </button>

        <button
          className="btn btn-outline flex-1"
          onClick={handleCancel}
          disabled={saving}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default LessonEditPage;
